import React, { useEffect, useState, createContext, useContext } from 'react';
import { useAuth } from '../auth/AuthContext';
export interface JobContact {
  id: string;
  jobId: string;
  role: 'contractor' | 'broker' | 'foreman' | 'trucking' | 'driver';
  displayName: string; // Will be the role name, not personal name
  isGroup?: boolean;
}
export interface JobMessage {
  id: string;
  jobId: string;
  senderId: string;
  senderRole: string;
  recipientId: string;
  recipientRole: string;
  content: string;
  timestamp: Date;
  isUrgent: boolean;
  isGroupMessage: boolean;
  readBy: string[];
}
export interface JobConversation {
  id: string;
  jobId: string;
  jobName: string;
  participants: JobContact[];
  messages: JobMessage[];
  lastMessageTimestamp: Date;
}
interface JobMessagingContextType {
  getJobContacts: (jobId: string, userRole: string) => JobContact[];
  sendJobMessage: (jobId: string, recipientId: string, content: string, isUrgent?: boolean) => void;
  getJobMessages: (jobId: string) => JobMessage[];
  markMessageAsRead: (messageId: string) => void;
  getUnreadMessageCount: (jobId?: string) => number;
  activeConversationJobId: string | null;
  setActiveConversationJobId: (jobId: string | null) => void;
}
// Create the context
const JobMessagingContext = createContext<JobMessagingContextType | undefined>(undefined);
// Sample data for job contacts
const MOCK_JOB_CONTACTS: Record<string, JobContact[]> = {
  'job-1': [{
    id: 'contractor-1',
    jobId: 'job-1',
    role: 'contractor',
    displayName: 'Contractor'
  }, {
    id: 'foreman-1',
    jobId: 'job-1',
    role: 'foreman',
    displayName: 'Foreman'
  }, {
    id: 'drivers-job-1',
    jobId: 'job-1',
    role: 'driver',
    displayName: 'Drivers',
    isGroup: true
  }, {
    id: 'trucking-1',
    jobId: 'job-1',
    role: 'trucking',
    displayName: 'Trucking Company'
  }],
  'job-2': [{
    id: 'contractor-2',
    jobId: 'job-2',
    role: 'contractor',
    displayName: 'Contractor'
  }, {
    id: 'foreman-2',
    jobId: 'job-2',
    role: 'foreman',
    displayName: 'Foreman'
  }, {
    id: 'drivers-job-2',
    jobId: 'job-2',
    role: 'driver',
    displayName: 'Drivers',
    isGroup: true
  }, {
    id: 'trucking-2',
    jobId: 'job-2',
    role: 'trucking',
    displayName: 'Trucking Company'
  }],
  'job-3': [{
    id: 'contractor-3',
    jobId: 'job-3',
    role: 'contractor',
    displayName: 'Contractor'
  }, {
    id: 'broker-1',
    jobId: 'job-3',
    role: 'broker',
    displayName: 'Broker'
  }, {
    id: 'foreman-3',
    jobId: 'job-3',
    role: 'foreman',
    displayName: 'Foreman'
  }, {
    id: 'drivers-job-3',
    jobId: 'job-3',
    role: 'driver',
    displayName: 'Drivers',
    isGroup: true
  }, {
    id: 'trucking-3',
    jobId: 'job-3',
    role: 'trucking',
    displayName: 'Trucking Company'
  }],
  'broker-job-1': [{
    id: 'contractor-4',
    jobId: 'broker-job-1',
    role: 'contractor',
    displayName: 'Contractor'
  }, {
    id: 'broker-2',
    jobId: 'broker-job-1',
    role: 'broker',
    displayName: 'Broker'
  }, {
    id: 'foreman-4',
    jobId: 'broker-job-1',
    role: 'foreman',
    displayName: 'Foreman'
  }, {
    id: 'drivers-broker-job-1',
    jobId: 'broker-job-1',
    role: 'driver',
    displayName: 'Drivers',
    isGroup: true
  }, {
    id: 'trucking-4',
    jobId: 'broker-job-1',
    role: 'trucking',
    displayName: 'Trucking Company'
  }],
  'direct-job-1': [{
    id: 'contractor-5',
    jobId: 'direct-job-1',
    role: 'contractor',
    displayName: 'Contractor'
  }, {
    id: 'foreman-5',
    jobId: 'direct-job-1',
    role: 'foreman',
    displayName: 'Foreman'
  }, {
    id: 'drivers-direct-job-1',
    jobId: 'direct-job-1',
    role: 'driver',
    displayName: 'Drivers',
    isGroup: true
  }, {
    id: 'trucking-4',
    jobId: 'direct-job-1',
    role: 'trucking',
    displayName: 'Trucking Company'
  }]
};
// Sample job data for mapping job IDs to names
const MOCK_JOB_NAMES: Record<string, string> = {
  'job-1': 'Downtown Commercial Complex Phase 1',
  'job-2': 'Highway 89 Expansion Project',
  'job-3': 'Residential Development Phase 2',
  'broker-job-1': 'East Valley Commercial Plaza',
  'direct-job-1': 'City Center Renovation'
};
export const JobMessagingProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user
  } = useAuth();
  const [messages, setMessages] = useState<JobMessage[]>([]);
  const [activeConversationJobId, setActiveConversationJobId] = useState<string | null>(null);
  // Get contacts for a specific job based on user role
  const getJobContacts = (jobId: string, userRole: string): JobContact[] => {
    const jobContacts = MOCK_JOB_CONTACTS[jobId] || [];
    // Filter contacts based on user role
    return jobContacts.filter(contact => {
      // Don't include the current user in the contacts list
      if (contact.role === userRole) {
        return false;
      }
      return true;
    });
  };
  // Send a message within a job context
  const sendJobMessage = (jobId: string, recipientId: string, content: string, isUrgent: boolean = false) => {
    if (!user) return;
    const recipient = MOCK_JOB_CONTACTS[jobId]?.find(c => c.id === recipientId);
    if (!recipient) return;
    const isGroupMessage = recipient.isGroup || false;
    const newMessage: JobMessage = {
      id: `msg-${Date.now()}`,
      jobId,
      senderId: user.id,
      senderRole: user.role,
      recipientId,
      recipientRole: recipient.role,
      content,
      timestamp: new Date(),
      isUrgent,
      isGroupMessage,
      readBy: [user.id]
    };
    setMessages(prev => [...prev, newMessage]);
  };
  // Get messages for a specific job
  const getJobMessages = (jobId: string): JobMessage[] => {
    if (!user) return [];
    return messages.filter(message => message.jobId === jobId && (message.senderId === user.id || message.recipientId === user.id || message.isGroupMessage && user.role === 'driver' && message.recipientRole === 'driver')).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  };
  // Mark a message as read
  const markMessageAsRead = (messageId: string) => {
    if (!user) return;
    setMessages(prev => prev.map(message => message.id === messageId && !message.readBy.includes(user.id) ? {
      ...message,
      readBy: [...message.readBy, user.id]
    } : message));
  };
  // Get count of unread messages
  const getUnreadMessageCount = (jobId?: string): number => {
    if (!user) return 0;
    return messages.filter(message => (!jobId || message.jobId === jobId) && message.recipientId === user.id && !message.readBy.includes(user.id)).length;
  };
  const contextValue: JobMessagingContextType = {
    getJobContacts,
    sendJobMessage,
    getJobMessages,
    markMessageAsRead,
    getUnreadMessageCount,
    activeConversationJobId,
    setActiveConversationJobId
  };
  return <JobMessagingContext.Provider value={contextValue} data-id="element-296">
      {children}
    </JobMessagingContext.Provider>;
};
export const useJobMessaging = () => {
  const context = useContext(JobMessagingContext);
  if (context === undefined) {
    throw new Error('useJobMessaging must be used within a JobMessagingProvider');
  }
  return context;
};